import moment, { Moment } from 'moment';
import { Holiday } from 'api/holiday-calendar/types';
import { ResourceAllocationApiDateRange } from 'api/resource-management/types';
import { TimeoffResponse } from 'api/timeoff/types';
import {
  format,
  formatterWithMinutesAndSeconds,
} from 'components/atoms/input-duration/formatter';
import { formatTimeWithSeconds } from 'components/organisms/timesheets/common/format-time';
import { WorkingDaysConfiguration } from 'components/organisms/user-capacity/types';
import { defaultFormat } from 'utils/date-field/formatter';
import { checkIfCurrentDateHasHoliday } from 'utils/holiday-calendar-utils';
import { checkIfCurrentDateHasTimeOff } from 'utils/time-off-utils';
import {
  isNonWorkingDay,
  numberOfWorkingDaysBetweenDates,
} from 'utils/working-days';
import { ViewPresetId } from '../scheduler-config/view-presets/constants';
import { Range } from '../types';

export const getDateAfter = (
  startDate: Moment,
  workingDaysConfiguration: WorkingDaysConfiguration,
  numberOfDays: number,
): Moment => {
  let endDate = startDate.add(numberOfDays - 1, 'days').endOf('day');

  while (true) {
    const numberOfWorkingDays = numberOfWorkingDaysBetweenDates(
      startDate,
      endDate,
      workingDaysConfiguration,
    );

    if (numberOfDays === numberOfWorkingDays) {
      break;
    }
    endDate = endDate.add(Math.abs(numberOfDays - numberOfWorkingDays), 'days');
  }

  return endDate;
};

export const shiftDateIfOnNonWorkingDay = (
  date: Moment,
  workingDaysConfiguration: WorkingDaysConfiguration,
  forward = true,
): Moment => {
  let tDate = date;

  while (isNonWorkingDay(tDate, workingDaysConfiguration)) {
    tDate = tDate.add(forward ? 1 : -1, 'day');
  }

  return tDate;
};

export const startDateParser = (date: Date | string): Moment =>
  moment(date).startOf('day');
export const endDateParser = (date: Date | string): Moment =>
  moment(date).endOf('day');

export const isDateBetweenRange = (
  date: Moment,
  { startDate, endDate }: { startDate: Moment; endDate: Moment },
): boolean => date.isBetween(startDate, endDate, 'day', '[]');

export const ignoreMinutesIfZero = (minutes: number): string =>
  minutes % 60 === 0
    ? format('hh', minutes / 60)
    : formatTimeWithSeconds(minutes);
export const formatTimeWithoutIgnoringNonZeroMinutes = (
  minutes: number,
): string =>
  minutes < 0
    ? `-${formatterWithMinutesAndSeconds(Math.abs(minutes))}`
    : formatterWithMinutesAndSeconds(minutes);
export const removeInsignificantTrailingZero = (
  number: number | string | undefined,
): string => {
  if (number == null || number === '') {
    return '0';
  }
  if (typeof number === 'string' && number.charAt(number.length - 1) === '.') {
    return number;
  }

  return parseFloat(parseFloat(number.toString()).toFixed(1)).toString();
};

export const expandRangeToIncludeNonWorkingDays = (
  { fromDate, toDate }: Range,
  workingDaysConfig: WorkingDaysConfiguration,
): Range => {
  let resultFromDate = fromDate;
  let resultToDate = toDate;

  if (isNonWorkingDay(fromDate.subtract(1, 'day'), workingDaysConfig)) {
    resultFromDate = shiftDateIfOnNonWorkingDay(
      fromDate.subtract(1, 'day'),
      workingDaysConfig,
      false,
    ).add(1, 'day');
  }
  if (isNonWorkingDay(toDate.add(1, 'day'), workingDaysConfig)) {
    resultToDate = shiftDateIfOnNonWorkingDay(
      toDate.add(1, 'day'),
      workingDaysConfig,
    ).subtract(1, 'day');
  }

  return {
    fromDate: resultFromDate,
    toDate: resultToDate,
  };
};

export const getCorrectedDateRangeBasedOnView = (
  range: Range,
  currentViewPreset: ViewPresetId,
  _workingDaysConfig: WorkingDaysConfiguration,
): Range => {
  const correctedRange: Range = {} as Range;

  switch (currentViewPreset) {
    case ViewPresetId.Day:
    case ViewPresetId.DayZoomed:
      correctedRange.fromDate = range.fromDate.startOf('day');
      correctedRange.toDate = range.toDate.endOf('day');
      break;
    case ViewPresetId.Month:
    case ViewPresetId.MonthZoomed:
      correctedRange.fromDate = range.fromDate.startOf('month');
      correctedRange.toDate = range.toDate.endOf('month');
      break;
    case ViewPresetId.Week:
    case ViewPresetId.WeekZoomed:
    default:
      // If Sunday, go to previous week's Monday, otherwise go to current week's Monday
      if (range.fromDate.day() === 0) {
        // Sunday: go to previous week's Monday
        correctedRange.fromDate = range.fromDate
          .startOf('week')
          .subtract(6, 'days');
      } else {
        // Otherwise: go to current week's Monday
        correctedRange.fromDate = range.fromDate.startOf('week').add(1, 'days');
      }
      correctedRange.toDate = range.toDate.endOf('week').add(1, 'days'); // this is to end week on Sunday

      break;
  }

  // COMMENTED OUT: Shifting non-working days logic
  return {
    fromDate: correctedRange.fromDate,
    // shiftDateIfOnNonWorkingDay(
    //   correctedRange.fromDate,
    //   workingDaysConfig,
    // ),
    toDate: correctedRange.toDate,
    // shiftDateIfOnNonWorkingDay(
    //   correctedRange.toDate,
    //   workingDaysConfig,
    //   false,
    // ),
  };
};

export const getCorrectedApiDateRangeBasedOnView = (
  range: Range,
  currentViewPreset: ViewPresetId,
  workingDaysConfig: WorkingDaysConfiguration,
): ResourceAllocationApiDateRange => {
  const correctedRange = getCorrectedDateRangeBasedOnView(
    range,
    currentViewPreset,
    workingDaysConfig,
  );

  return {
    startDate: defaultFormat(correctedRange.fromDate),
    endDate: defaultFormat(correctedRange.toDate),
  };
};

/**
 * Checks if a date range contains Saturday and/or Sunday
 * @param startDate - Start date of the range (Moment object)
 * @param endDate - End date of the range (Moment object)
 * @returns Object with hasSaturday and hasSunday boolean values
 */
export const hasWeekendDaysInRange = (
  startDate: Moment,
  endDate: Moment,
): { hasSaturday: boolean; hasSunday: boolean } => {
  const startMoment = moment(startDate).startOf('day');
  const endMoment = moment(endDate).endOf('day');
  const daysDiff = endMoment.diff(startMoment, 'days');

  let hasSat = false;
  let hasSun = false;

  for (let i = 0; i <= daysDiff; i += 1) {
    const currentDate = startMoment.clone().add(i, 'days');
    const dayOfWeek = currentDate.day();

    if (dayOfWeek === 6) {
      hasSat = true;
    } else if (dayOfWeek === 0) {
      hasSun = true;
    }

    if (hasSat && hasSun) {
      break;
    }
  }

  return { hasSaturday: hasSat, hasSunday: hasSun };
};

export const isDateRangeWeekendOnly = (
  startDate: Moment,
  endDate: Moment,
): boolean => {
  const startMoment = moment(startDate).startOf('day');
  const endMoment = moment(endDate).startOf('day');

  if (endMoment.isBefore(startMoment)) {
    return false;
  }

  const daysDiff = endMoment.diff(startMoment, 'days');

  for (let i = 0; i <= daysDiff; i += 1) {
    const dayOfWeek = startMoment.clone().add(i, 'days').day();

    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      return false;
    }
  }

  return true;
};

export const isDateRangeFullyCoveredByHolidayOrTimeOff = (
  startDate: Moment,
  endDate: Moment,
  holidays?: Holiday[],
  timeOffs?: TimeoffResponse[],
): boolean => {
  const startMoment = moment(startDate).startOf('day');
  const endMoment = moment(endDate).startOf('day');

  if (endMoment.isBefore(startMoment)) {
    return false;
  }

  const holidayList = holidays ?? [];
  const hasActiveTimeOff = timeOffs?.some((entry) => !entry.deleted) ?? false;

  if (holidayList.length === 0 && !hasActiveTimeOff) {
    return false;
  }

  const daysDiff = endMoment.diff(startMoment, 'days');

  for (let i = 0; i <= daysDiff; i += 1) {
    const currentDate = startMoment.clone().add(i, 'days');

    if (
      !checkIfCurrentDateHasHoliday(holidayList, currentDate) &&
      !checkIfCurrentDateHasTimeOff(timeOffs, currentDate)
    ) {
      return false;
    }
  }

  return true;
};

export const getEnhancedWeekendInclusionFlags = (
  startDate: Moment,
  endDate: Moment,
  includeSaturday: boolean,
  includeSunday: boolean,
): { isSaturdayIncluded: boolean; isSundayIncluded: boolean } => {
  const { hasSaturday, hasSunday } = hasWeekendDaysInRange(startDate, endDate);

  return {
    isSaturdayIncluded: hasSaturday && includeSaturday,
    isSundayIncluded: hasSunday && includeSunday,
  };
};

export const getWorkingDaysPerWeek = (
  startDate: Moment,
  endDate: Moment,
  includeSaturday: boolean,
  includeSunday: boolean,
  workingDaysConfig: WorkingDaysConfiguration,
): number => {
  let workingDaysPerWeek =
    Object.values(workingDaysConfig).filter(Boolean).length;

  const { isSaturdayIncluded, isSundayIncluded } =
    getEnhancedWeekendInclusionFlags(
      startDate,
      endDate,
      includeSaturday,
      includeSunday,
    );

  if (isSaturdayIncluded) {
    workingDaysPerWeek += 1;
  }
  if (isSundayIncluded) {
    workingDaysPerWeek += 1;
  }

  return Math.min(workingDaysPerWeek, 7);
};
